/* eslint-disable @nx/enforce-module-boundaries*/
import capitalize from 'lodash/capitalize';
import { AvSpeichernKDVDaten, AvSpeichernProdukt, AvUpdateRequestModel, Role, } from '@ps20/bff-models';
import { App, getSpartenTypeByApp } from '@ps20-frontend/frontend-models';
import cloneDeep from 'lodash/cloneDeep';
import { IAppState as DashboardAppState } from '@ps20-frontend/apps/dashboard-store';
import { IAppState as CheckoutAppState, selectActiveVermittlerNummer } from '@ps20-frontend/apps/checkout-store';
import { IAppState as ToolbarAppState } from '@ps20-frontend/apps/toolbar-store';
import {
  Datenhaltung,
  isAzam,
  SavedState,
  SavedStateWrapper,
  WrappedSavedStates,
} from '@ps20-frontend/shop-integration-redux';
import { IAppState as ShopAppState } from '../../localStore';
import { LZString } from '../helpers/stringCompressor';
import { isSdwSession } from '../../selectors/transientState.selector';

type DashboardBaseProduct = DashboardAppState['warenkorb'][0];

function getVorgangsIdByRole(datenhaltung: Datenhaltung, role: Role, azamVorgangsId: string): string {
  return ({
    [Role.MAKLERBETREUER]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.MAKLER]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.UVM]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.NEBENBERUFSVERTRETER]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.UNDERWRITER_UNFALL]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.OLB]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.GENO]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.UWPSHRK]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.AGENTURANGESTELLTER]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.LEITENDER_KUNDENBETREUER]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.VTTEL]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.VTGEV]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.VTKUS]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.VTKVM]: datenhaltung.azamVorgangsId || azamVorgangsId,
    [Role.VERTRETER]: datenhaltung.kdv.vorId,
  } as Partial<Record<Role, string>>)[role];
}

export function generateSpeichernRequest(
  shopState: ShopAppState,
  dashboardState: DashboardAppState,
  checkoutState: CheckoutAppState,
  toolbarState: ToolbarAppState,
  appStates: WrappedSavedStates,
  keepGisDaten: boolean,
  forwardToUnderwriter?: boolean,
): AvUpdateRequestModel {
  const kdvDaten: AvSpeichernKDVDaten = {
    metadaten: {
      antragId: checkoutState.sammelantragsnummer,
      gesamtbeitrag: dashboardState.gesamtbeitrag,
      notiz: toolbarState.speichern.vermerk,
      produkte: createProduktList(dashboardState.warenkorb, shopState.transientState.role),
      vermittlerid: selectActiveVermittlerNummer(checkoutState.vermittler),
      zahlweise: toolbarState.zahlungsoptionen.zahlungsweise,
    },
    sessiondaten: {
      perid: shopState.transientState.datenhaltung.kdv.perId,
      amisupd: shopState.transientState.datenhaltung.kdv.amisupd,
      role: shopState.transientState.datenhaltung.sdw?.role,
      partyId: shopState.transientState.datenhaltung.sdw?.partyId,
      vorgangid: getVorgangsIdByRole(
        shopState.transientState.datenhaltung,
        shopState.transientState.role,
        shopState.azamVorgangsId,
      ),
    },
  };
  const sensitiveStores = getSensitiveStores(appStates);
  const stores: WrappedSavedStates = appStates.map(appState => {
    const stateToModify = cloneDeep(appState.state);

    delete stateToModify.kdv;
    delete stateToModify.transientState;
    delete stateToModify.canNavigate;
    if (!stateToModify.vertragsoptionen?.beibehalten && !keepGisDaten) {
      delete stateToModify.gisDaten;
    }
    const speichern = stateToModify.speichern as Record<string, unknown>;
    if (speichern) {
      delete speichern.speicherModalOpen;
      delete speichern.speicherStatus;
    }

    deleteSensitiveData(appState, stateToModify);
    return {
      appId: appState.appId,
      angebotsId: appState.angebotsId,
      state: stateToModify,
    };
  });
  const stringifiedStores = JSON.stringify(stores);
  const compressedStores = LZString.compressToBase64(stringifiedStores);
  const avUpdateRequestModel: AvUpdateRequestModel = {
    stores: compressedStores,
    kdv: kdvDaten,
    cryptId: shopState.transientState.datenhaltung.av.cryptId,
    angebotsBuendelId: shopState.transientState.datenhaltung.av.angebotsBuendelId,
    forwardToUnderwriter: forwardToUnderwriter ?? null,
    isAzam: isAzam(shopState.transientState.role),
    isSdwSession: isSdwSession(shopState),
  };

  if (sensitiveStores.unfAngaben?.vpAngabenListe?.length > 0) {
    const stringifiedSensitiveStores = JSON.stringify(sensitiveStores);
    avUpdateRequestModel.sensitiveStores = LZString.compressToBase64(stringifiedSensitiveStores);
  }
  return avUpdateRequestModel;
}

function createProduktList(products: DashboardBaseProduct[], role: string): AvSpeichernProdukt[] {
  return products
    .filter(product => product.appId !== App.TGV)
    .map(product => {
      return {
        produkt: getSpartenTypeByApp(product.appId),
        beitrag: product.bruttopreis > 0 ? Number.parseFloat(product.bruttopreis?.toFixed(2)) : null,
        tarifierungsmerkmal: isAzam(role)
          ? mapTarifierungsmerkmal(product).replaceAll('<br>', ' ')
          : null,
      };
    });
}

function mapTarifierungsmerkmal(product: DashboardBaseProduct): string {
  if (product.versichertesRisiko && product.ausgewaehlteLinie && product.produktKdvId) {
    const capProduktlinienname = capitalize(product.ausgewaehlteLinie);
    return `${product.produktKdvId} ` + capProduktlinienname + ` ${product.versichertesRisiko}`;
  }
  return '';
}

function deleteSensitiveData(appState: SavedStateWrapper, stateToModify: SavedState): void {
  if (appState.appId === App.SHOP_CHECKOUT) {
    stateToModify.unfAngaben?.vpAngabenListe?.forEach(element => {
      if (element.pflegeFrageAntwort !== null && element.pflegeFrageAntwort !== undefined) {
        delete element.pflegeFrageAntwort;
      }
      if (element.hasBeeintraechtigungen !== null && element.hasBeeintraechtigungen !== undefined) {
        delete element.hasBeeintraechtigungen;
      }
      if (element.beeintraechtigungen !== null && element.beeintraechtigungen !== undefined) {
        delete element.beeintraechtigungen;
      }
    });
  }
}

function getSensitiveStores(appStates: SavedStateWrapper[]): SavedState {
  const sensitiveStores: SavedState = {
    unfAngaben: {
      vpAngabenListe: [],
    },
  } as SavedState;
  appStates.forEach(appState => {
    if (appState.appId === App.SHOP_CHECKOUT) {
      appState.state.unfAngaben?.vpAngabenListe?.forEach(element => {
        if (
          (element.pflegeFrageAntwort !== null && element.pflegeFrageAntwort !== undefined) ||
          (element.hasBeeintraechtigungen !== null &&
            element.hasBeeintraechtigungen !== undefined) ||
          (element.beeintraechtigungen !== null && element.beeintraechtigungen !== undefined)
        ) {
          sensitiveStores.unfAngaben.vpAngabenListe.push(element);
        }
      });
    }
  });
  return sensitiveStores;
}
